import { z } from 'zod';
import { runStatus, uuid } from './primitives';
import { dailyCostBreakdown, isoDate } from './usage';

/**
 * Dashboard / stats wire contracts (Master Spec §14, dashboard/stats API). Two
 * read-only aggregate surfaces power a home dashboard:
 *
 *  1. `workspaceDashboard` — a WORKSPACE-SCOPED (tenant-safe) snapshot every member
 *     sees: agent counts, run totals + status mix + a run time series, the top
 *     agents by run volume, and the existing daily cost breakdown (reused verbatim so
 *     the web renders it with the same stacked-cost chart).
 *  2. `platformStats` — an OPERATOR-ONLY, cross-tenant view: platform totals, the most
 *     active users + agents, and the platform-wide daily spend trend. Emails are
 *     operator-visible here by design; no secrets ever cross this boundary (I6).
 *
 * Decisions baked into these shapes (shared with the usage contract):
 *  - Time series (`runsOverTime`, `spendTrend`) are bucketed by **UTC** calendar day,
 *    zero-filled so the window is a continuous range for a chart x-axis.
 *  - `windowDays` echoes the server's fixed rolling window for the "recent" metrics.
 */

// ── Workspace dashboard (member, tenant-scoped) ──────────────────────────────

/** Agent inventory for a workspace (`total` includes every status). */
export const dashboardAgentCounts = z.object({
  total: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  archived: z.number().int().nonnegative(),
});
export type DashboardAgentCounts = z.infer<typeof dashboardAgentCounts>;

/** A run-status bucket for the status-mix donut/bar (only statuses present appear). */
export const runStatusCount = z.object({
  status: runStatus,
  count: z.number().int().nonnegative(),
});
export type RunStatusCount = z.infer<typeof runStatusCount>;

/** One day's run count in the `runsOverTime` series (UTC calendar day). */
export const dailyRunCount = z.object({
  date: isoDate,
  count: z.number().int().nonnegative(),
});
export type DailyRunCount = z.infer<typeof dailyRunCount>;

/**
 * A "top agents by run volume" leaderboard row. `agentName` is a placeholder for a
 * since-deleted agent (the run's `agent_id` is a forward reference, §6.4).
 */
export const runsByAgentEntry = z.object({
  agentId: uuid,
  agentName: z.string(),
  count: z.number().int().nonnegative(),
});
export type RunsByAgentEntry = z.infer<typeof runsByAgentEntry>;

/** Run totals + the status mix. `total` is all-time; `windowTotal` is the last N days. */
export const dashboardRuns = z.object({
  total: z.number().int().nonnegative(),
  windowTotal: z.number().int().nonnegative(),
  byStatus: z.array(runStatusCount),
});
export type DashboardRuns = z.infer<typeof dashboardRuns>;

/**
 * Quick-chat (agent-chat) activity for a workspace — reported SEPARATELY from agent runs
 * (§14.5/§21). A chat turn is backed by an EPHEMERAL `agent_runs` row (`agent_id = null`)
 * that only exists so the turn can be routed through the ToolGateway; those substrate runs
 * are DELIBERATELY excluded from every `dashboardRuns` count, so the chat feature never
 * inflates the run metrics. This block reports the chat feature on its own terms:
 *  - `sessions` — distinct quick-chat sessions (`chat_sessions` rows), all-time.
 *  - `messages` — persisted chat messages (user + assistant turns), all-time.
 *  - `messagesWindow` — those messages in the last `windowDays` days (the "recent" signal).
 *  - `toolUses` — tool calls the chat made: `tool_call` steps on the `agent_id`-null
 *    substrate runs (the same authoritative signal the ops `tool_call` metric counts).
 */
export const dashboardChat = z.object({
  sessions: z.number().int().nonnegative(),
  messages: z.number().int().nonnegative(),
  messagesWindow: z.number().int().nonnegative(),
  toolUses: z.number().int().nonnegative(),
});
export type DashboardChat = z.infer<typeof dashboardChat>;

/**
 * The workspace home-dashboard response (§14). Everything a member's dashboard needs
 * in one call: agent counts, run totals/status/series, top agents, the daily cost
 * breakdown (the SAME `dailyCostBreakdown` shape the Runs-page cost chart consumes), and
 * the quick-chat activity block (§14.5) reported separately from agent runs.
 */
export const workspaceDashboard = z.object({
  /** The rolling window (days) for `windowTotal`, `runsOverTime`, `cost`, and `chat`. */
  windowDays: z.number().int().positive(),
  agents: dashboardAgentCounts,
  runs: dashboardRuns,
  runsOverTime: z.array(dailyRunCount),
  runsByAgent: z.array(runsByAgentEntry),
  cost: dailyCostBreakdown,
  chat: dashboardChat,
});
export type WorkspaceDashboard = z.infer<typeof workspaceDashboard>;

// ── Platform stats (operator-only, cross-tenant) ─────────────────────────────

/** Platform-wide counts. `runsWindow` is the last N days; the rest are all-time. */
export const platformTotals = z.object({
  users: z.number().int().nonnegative(),
  workspaces: z.number().int().nonnegative(),
  activeAgents: z.number().int().nonnegative(),
  runsAllTime: z.number().int().nonnegative(),
  runsWindow: z.number().int().nonnegative(),
});
export type PlatformTotals = z.infer<typeof platformTotals>;

/** A most-active-user leaderboard row (attributed to the workspaces a user owns). */
export const activeUserStat = z.object({
  userId: uuid,
  name: z.string(),
  email: z.string(),
  runCount: z.number().int().nonnegative(),
});
export type ActiveUserStat = z.infer<typeof activeUserStat>;

/** A most-active-agent leaderboard row (name + owning workspace + run volume). */
export const activeAgentStat = z.object({
  agentId: uuid,
  agentName: z.string(),
  workspaceId: uuid,
  workspaceName: z.string(),
  runCount: z.number().int().nonnegative(),
});
export type ActiveAgentStat = z.infer<typeof activeAgentStat>;

/** One day's platform-wide total spend (UTC calendar day) in the `spendTrend` series. */
export const dailyTotalCost = z.object({
  date: isoDate,
  totalUsd: z.number().nonnegative(),
});
export type DailyTotalCost = z.infer<typeof dailyTotalCost>;

/**
 * Platform-wide quick-chat activity (cross-tenant, operator only) — the un-scoped sibling
 * of `dashboardChat`, reported separately from agent runs (§14.5). All-time counts across
 * every workspace: distinct sessions, persisted messages, and chat tool calls (`tool_call`
 * steps on `agent_id`-null substrate runs). Counts only — no message content ever crosses
 * this boundary (I6).
 */
export const platformChat = z.object({
  sessions: z.number().int().nonnegative(),
  messages: z.number().int().nonnegative(),
  toolUses: z.number().int().nonnegative(),
});
export type PlatformChat = z.infer<typeof platformChat>;

/**
 * The operator platform-stats response (§14). Cross-tenant totals + two leaderboards +
 * the platform-wide daily spend trend. Operator-gated (`requireOperator`); every
 * cross-tenant read is confined to allow-listed `db/raw` helpers (I5/§6.6).
 */
export const platformStats = z.object({
  /** The rolling window (days) for `runsWindow`, the leaderboards, and `spendTrend`. */
  windowDays: z.number().int().positive(),
  totals: platformTotals,
  mostActiveUsers: z.array(activeUserStat),
  mostActiveAgents: z.array(activeAgentStat),
  spendTrend: z.array(dailyTotalCost),
  /** Platform-wide quick-chat activity, reported separately from agent runs (§14.5). */
  chat: platformChat,
});
export type PlatformStats = z.infer<typeof platformStats>;
